/**
 * 6.D "Calendar" view tab — reuses the app's month calendar (`components/Calendar/Calendar`, redux-connected,
 * honours `calendar_first_day`) exactly like today's `ScheduleCalendarView`: each date shows its *effective*
 * hours (weekly rows projected on the day, dated overrides replacing them); clicking a date opens an inline
 * editor that writes a `type='custom'` row (`dated` = `DD-MM-YYYY`) — the port of `ScheduleDayDropdown` +
 * `DayScheduleSelectorModal.saveSchedule`.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Calendar from "../../../../../components/Calendar/Calendar";
import { FieldError } from "../../ui/primitives";
import { DEFAULT_WORK_END, DEFAULT_WORK_START } from "../../state/constants";
import { validateIntervals } from "../../state/validation";
import { removeDatedRows, setDatedRows } from "../shared/useScheduleOps";

const toDate = (year, monthIdx, day, hhmm) => {
  const [h, m] = String(hhmm || "00:00").split(":");
  return new Date(year, monthIdx, day, parseInt(h, 10) || 0, parseInt(m, 10) || 0, 0);
};

/** Effective intervals for a calendar date (dated override wins, else the weekday row). */
export const intervalsForDate = (rows, m) => {
  const dated = m.format("DD-MM-YYYY");
  const override = (rows || []).find((r) => r.dated === dated);
  if (override) return { intervals: override.schedules || [], custom: true };
  const dayId = m.isoWeekday(); // Monday = 1 … Sunday = 7 (today's weekday_id)
  const weekly = (rows || []).find((r) => !r.dated && r.dayId === dayId);
  return { intervals: weekly ? weekly.schedules || [] : [], custom: false };
};

export const monthEvents = (rows, month /* 1-12 */, year) => {
  const events = [];
  const days = moment(`${year}-${month}`, "YYYY-M").daysInMonth();
  for (let d = 1; d <= days; d += 1) {
    const m = moment({ year, month: month - 1, day: d });
    const { intervals } = intervalsForDate(rows, m);
    intervals.forEach((s) => {
      if (!s.start_time || !s.end_time) return;
      events.push({ title: `${s.start_time} - ${s.end_time}`, start: toDate(year, month - 1, d, s.start_time), end: toDate(year, month - 1, d, s.end_time) });
    });
  }
  return events;
};

const DayEditor = ({ dated, rows, onChange, onClose }) => {
  const m = moment(dated, "DD-MM-YYYY");
  const initial = intervalsForDate(rows, m);
  const [slots, setSlots] = useState(() =>
    initial.intervals.length ? initial.intervals.map((s) => ({ ...s })) : [{ start_time: DEFAULT_WORK_START, end_time: DEFAULT_WORK_END }]
  );
  const errors = validateIntervals([{ dated, schedules: slots }]);
  const invalid = Object.keys(errors).length > 0;

  return (
    <div className="eb-day-editor" role="dialog" aria-label={`Hours for ${m.format("LL")}`}>
      <header>
        <strong>{m.format("dddd, LL")}</strong>
        <small>{initial.custom ? "Custom hours for this date" : "Using weekly hours"}</small>
      </header>
      {slots.map((s, idx) => {
        const err = errors[`dated:${dated}:${idx}`];
        return (
          <React.Fragment key={idx}>
            <div className="eb-interval-line">
              <input type="time" step="900" value={s.start_time || ""} aria-label="Start time" onChange={(e) => setSlots(slots.map((x, i) => (i === idx ? { ...x, start_time: e.target.value } : x)))} />
              <span>-</span>
              <input type="time" step="900" value={s.end_time || ""} aria-label="End time" onChange={(e) => setSlots(slots.map((x, i) => (i === idx ? { ...x, end_time: e.target.value } : x)))} />
              {idx === 0 ? (
                <button type="button" className="eb-row-icon" title="Add hours" onClick={() => setSlots([...slots, { start_time: "", end_time: "" }])}>
                  +
                </button>
              ) : (
                <button type="button" className="eb-row-icon" title="Remove hours" onClick={() => setSlots(slots.filter((_, i) => i !== idx))}>
                  {"\u2212"}
                </button>
              )}
            </div>
            <FieldError message={err} />
          </React.Fragment>
        );
      })}
      <div className="eb-day-editor-actions">
        <button type="button" className="eb-outline-small" onClick={onClose}>
          Cancel
        </button>
        {initial.custom ? (
          <button type="button" className="eb-outline-small" onClick={() => onChange(removeDatedRows(rows, dated))}>
            Use weekly hours
          </button>
        ) : null}
        <button type="button" className="eb-outline-small" onClick={() => onChange(setDatedRows(rows, dated, slots))} disabled={invalid}>
          Save hours for this date
        </button>
      </div>
    </div>
  );
};

const CustomDates = ({ rows, onChange, readOnly }) => {
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const [view, setView] = useState(() => ({ month: moment().month() + 1, year: moment().year() }));
  const [editing, setEditing] = useState(null); // 'DD-MM-YYYY'
  const events = useMemo(() => monthEvents(rows, view.month, view.year), [rows, view.month, view.year]);

  // the shared Calendar stores the callbacks in redux on mount → keep them stable
  const onNavigate = useCallback((date) => {
    const m = moment(date);
    setView({ month: parseInt(m.format("M"), 10), year: parseInt(m.format("YYYY"), 10) });
  }, []);
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const onSelectSlot = useCallback((dated) => {
    if (readOnlyRef.current) return;
    setEditing(dated);
  }, []);

  useEffect(() => {
    if (readOnly) setEditing(null);
  }, [readOnly]);

  return (
    <div className="eb-calendar-tab">
      <Calendar views={["month"]} events={events} shortEventSlot onNavigate={onNavigate} onSelectSlot={onSelectSlot} />
      {editing ? (
        <DayEditor
          key={editing}
          dated={editing}
          rows={rowsRef.current}
          onClose={() => setEditing(null)}
          onChange={(next) => {
            onChange(next);
            setEditing(null);
          }}
        />
      ) : null}
      <small className="eb-helper">Dates with custom hours override the weekly schedule. Click a date to set its hours.</small>
    </div>
  );
};

export default CustomDates;
